import { CtcAction, CtcRole } from "./ctcLifecycle";
import { isCtcActive } from "./ctcMasterData";

export const CTC_DESK_DOMAINS = ["kfhbrokerage.com", "kfhcapital.com.kw"];

/** Bank makers and checkers don't see the broker commission (order page, reports); CTC staff and admins do. */
export const ctcHidesBrokerCommission = (user: { isDesk: boolean; isAdmin: boolean; roles: readonly string[] }) =>
    !user.isDesk && !user.isAdmin && user.roles.some((r) => r === "maker" || r === "checker");

/** Who may add other documents to an order they can see (order page → Documents): every working role; not view-only users. */
export const ctcCanAddAttachments = (user: { isAdmin: boolean; roles: readonly string[] }) =>
    user.isAdmin || user.roles.some((r) => r !== "viewer" && r !== "beneficiary");
export const CTC_MAX_ADDED_ATTACHMENTS = 2; // per upload

/** CTC roles a set of custom claims grants. `admin` (portal) and `ctc_admin` both yield the admin role. */
export function ctcRolesFromClaims(claims: Partial<Record<"ctc_maker" | "ctc_checker" | "ctc_supervisor" | "ctc_dealer" | "ctc_backoffice" | "ctc_admin" | "ctc_viewer" | "admin", unknown>> | undefined): CtcRole[] {
    if (!claims) return [];
    const roles: CtcRole[] = [];
    if (claims.ctc_maker) roles.push("maker");
    if (claims.ctc_checker) roles.push("checker");
    if (claims.ctc_supervisor) roles.push("supervisor");
    if (claims.ctc_dealer) roles.push("dealer");
    if (claims.ctc_backoffice) roles.push("backoffice");
    if (claims.ctc_admin || claims.admin) roles.push("admin");
    if (claims.ctc_viewer) roles.push("viewer");
    return roles;
}

/** Roles that work for one customer (create / check its orders) rather than for the desk. */
const LANE_ROLES: ReadonlySet<CtcRole> = new Set<CtcRole>(["maker", "checker"]);

/**
 * A customer's Microsoft group ids (ctc_customers.microsoftGroupId): one id (older records) or a list
 * (e.g. a makers group and a checkers group). Trimmed, lower-case, no blanks or repeats.
 */
export function ctcCustomerGroupIds(customer: { microsoftGroupId?: string | string[] | null }): string[] {
    const raw = customer.microsoftGroupId;
    const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
    return [...new Set(list.map((id) => `${id ?? ""}`.trim().toLowerCase()).filter(Boolean))];
}

/**
 * Customers with at least one Microsoft group the user belongs to. Group ids are GUIDs: compared
 * trimmed and case-insensitively. Retired and deleted customers never match.
 */
export function ctcGroupCustomerIds(
    userGroupIds: string[],
    customers: { id: string; microsoftGroupId?: string | string[] | null; retired?: boolean; deleted?: boolean }[],
): string[] {
    const mine = new Set(userGroupIds.map((id) => id.trim().toLowerCase()).filter(Boolean));
    return customers
        .filter((c) => isCtcActive(c) && ctcCustomerGroupIds(c).some((id) => mine.has(id)))
        .map((c) => c.id);
}

/**
 * Which side of the desk a CTC user is on.
 * - Maker/checker-only users in exactly one customer's Microsoft group act for that customer
 *   (and so only see it), whatever their email domain.
 * - Maker/checker-only users matching several customers get no binding (fail closed): one group is
 *   one customer, so this is a misconfiguration (duplicate group id) or multi-membership. Falling
 *   through would make a desk-domain user desk staff (every customer) or leave a bank user bound by
 *   domain to a customer other than the ones they can create orders for.
 * - Otherwise as before: KFH bank users by email domain (domainCustomerIds), brokerage /
 *   KFH Capital staff are the desk and see every customer.
 * A missing/ambiguous bank mapping must never turn a branch user into desk staff.
 */
export function resolveCtcBinding(
    email: string,
    domainCustomerIds: string[],
    { groupCustomerIds = [], roles = [] }: { groupCustomerIds?: string[]; roles?: CtcRole[] } = {},
): { ctc_customer_id?: string; ctc_staff?: boolean } {
    const laneOnly = roles.length > 0 && roles.every((role) => LANE_ROLES.has(role));
    if (laneOnly && groupCustomerIds.length === 1) return { ctc_customer_id: groupCustomerIds[0] };
    if (laneOnly && groupCustomerIds.length > 1) return {};
    const domain = email.trim().toLowerCase().split("@")[1];
    if (domain === "kfh.com") return domainCustomerIds.length === 1 ? { ctc_customer_id: domainCustomerIds[0] } : {};
    return CTC_DESK_DOMAINS.includes(domain) ? { ctc_staff: true } : {};
}

/**
 * Makers and checkers may create / check orders only for customers whose Microsoft group they're
 * in (ctc_group_customer_ids claim). A customer without a group therefore has no makers/checkers.
 * Admins are not restricted.
 */
export function ctcLaneCustomerRestriction(
    actor: { isAdmin: boolean; groupCustomerIds?: string[] },
    customerId: string,
): string | undefined {
    if (actor.isAdmin) return undefined;
    return actor.groupCustomerIds?.includes(customerId) ?
        undefined :
        "You can only create or check orders for customers whose Microsoft group you are a member of";
}

export function ctcActorRestriction(
    actor: { email: string; roles: CtcRole[]; isAdmin: boolean },
    po: { makerEmail: string; transferRequestedByEmail?: string; assignedDealerEmail?: string },
    action: CtcAction,
    options: { allowSelfCheck?: boolean } = {},
): string | undefined {
    const email = actor.email.toLowerCase();
    // A user may check their own order (or transfer request) when they have a desk email domain
    // (CTC_DESK_DOMAINS) and hold both the maker and the checker role, or in the test environment
    // (allowSelfCheck). Otherwise (e.g. KFH bank users) a different checker must approve.
    const selfCheck = options.allowSelfCheck === true || (CTC_DESK_DOMAINS.includes(email.split("@")[1]) && actor.roles.includes("maker") && actor.roles.includes("checker"));
    if (action === CtcAction.approve && !selfCheck && email === po.makerEmail.toLowerCase()) return "A different checker must approve the maker's order";
    if (action === CtcAction.approve_transfer && (!po.transferRequestedByEmail || (!selfCheck && email === po.transferRequestedByEmail.toLowerCase()))) {
        return "A different checker must approve the transfer request";
    }
    const dealerActions = [CtcAction.confirm_purchase, CtcAction.confirm_transfer, CtcAction.confirm_sell,
    CtcAction.complete_collection, CtcAction.beneficiary_approve_transfer, CtcAction.beneficiary_reject_transfer,
    CtcAction.beneficiary_sell, CtcAction.beneficiary_collect, CtcAction.beneficiary_reject_sell,
    CtcAction.beneficiary_change_answer, CtcAction.reject_transfer_by_ctc, CtcAction.reject_sell_by_ctc];
    if (dealerActions.includes(action) && email !== po.assignedDealerEmail?.toLowerCase()) {
        return "Only the assigned dealer may act; a supervisor must reassign the order first";
    }
    if (action === CtcAction.reject_by_ctc && !actor.isAdmin && !actor.roles.includes("supervisor") && email !== po.assignedDealerEmail?.toLowerCase()) {
        return "Only the assigned dealer or a supervisor may reject this order";
    }
    if ([CtcAction.submit, CtcAction.delete].includes(action) && !actor.isAdmin && actor.roles.includes("maker") && email !== po.makerEmail.toLowerCase()) {
        return "Only the maker may submit or delete this draft";
    }
    return undefined;
}
